import React, { createContext, useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const queryClient = useQueryClient();

  // Fetch user data
  const { data: user, isLoading } = useQuery(
    ['user', token],
    () => api.get('/auth/me').then(res => res.data.user),
    {
      enabled: !!token,
      retry: false,
      onError: (err) => {
        if (err.response?.status === 401) {
          logout();
        }
      }
    }
  );

  const login = async (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
    queryClient.invalidateQueries(['user']);
    toast.success('Successfully logged in!');
    
    // Return a promise to ensure token is set
    return new Promise((resolve) => {
      setTimeout(() => resolve(), 50);
    });
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('token');
    queryClient.clear();
    toast.success('Logged out successfully');
  };

  const updateUser = (userData) => {
    queryClient.setQueryData(['user', token], userData);
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token && !!user,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
